// ABOUTME: Displays stored MCP client identity and explicitly requested OAuth scopes.
// ABOUTME: Better Auth verifies the signed authorization query when consent is submitted.
import { useEffect, useState } from "react";
import { authClient } from "../auth";
import { authorizationRedirect, message, request, safeLink } from "../api";
import type { Session } from "../api";
import { BrandLogo } from "./BrandLogo";
import { Button, Notice } from "./ui/controls";
import { ArrowRight, Check, CircleAlert, Link2, ShieldCheck } from "./ui/icons";

const scopeDescriptions: Record<string, string> = {
  openid: "Identify your NovaName account",
  profile: "Read your profile name",
  email: "Read your account email",
  offline_access:
    "Keep this app connected by refreshing access until you revoke the connection or sign-in session",
  "novaname:read":
    "Read your private projects, names, dated reports, and usage",
  "novaname:write":
    "Create projects, save brief versions, and update name decisions",
  "novaname:generate": "Generate names using your shared monthly allowance",
  "novaname:check":
    "Quote and submit research using your shared check allowance",
  "novaname:spend":
    "Spend purchased check credits after you separately enable a spending cap",
  "novaname:export": "Download a complete export of your private account data",
};
type Client = {
  client_id: string;
  client_name?: string;
  client_uri?: string;
  policy_uri?: string;
  tos_uri?: string;
};

export function ConsentPage({ session }: { session: Session }) {
  const query = new URLSearchParams(location.search);
  const clientId = query.get("client_id");
  const requested = [
    ...new Set((query.get("scope") ?? "").split(" ").filter(Boolean)),
  ];
  const [client, setClient] = useState<Client | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [accepted, setAccepted] = useState(
    requested.filter(
      (scope) => !["novaname:spend", "novaname:export"].includes(scope),
    ),
  );
  useEffect(() => {
    let alive = true;
    if (!clientId) {
      setError(
        "This authorization request has no client identity. Restart the connection from your MCP client.",
      );
      return;
    }
    void request<Client>(
      `/api/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`,
    )
      .then((value) => {
        if (alive) setClient(value);
      })
      .catch((reason) => {
        if (alive) setError(message(reason));
      });
    return () => {
      alive = false;
    };
  }, [clientId]);
  async function consent(accept: boolean) {
    setBusy(accept ? "accept" : "deny");
    setError("");
    try {
      const result = await authClient.oauth2.consent({
        accept,
        ...(accept ? { scope: accepted.join(" ") } : {}),
      });
      if (result.error)
        throw new Error(
          result.error.message ??
            "The authorization request was rejected. Restart the connection from your client.",
        );
      const href = authorizationRedirect(result.data?.url);
      if (!href)
        throw new Error(
          "Authorization did not return a valid client redirect. Restart the connection.",
        );
      location.assign(href);
    } catch (reason) {
      setError(message(reason));
      setBusy("");
    }
  }
  const clientUrl = safeLink(client?.client_uri);
  return (
    <main className="consent-layout">
      <a className="logo-link" href="/" aria-label="NovaName home">
        <BrandLogo />
      </a>
      <section className="consent-panel">
        <span className="tag">Connect your naming workspace</span>
        <h1>
          {client
            ? `${client.client_name ?? client.client_id} would like access.`
            : "Review this connection."}
        </h1>
        <p>
          Signed in as <strong>{session.user.email}</strong>. Client identity is
          loaded from its registered record.
        </p>
        {clientUrl && (
          <p>
            <a href={clientUrl} target="_blank" rel="noopener noreferrer">
              Client website <Link2 />
            </a>
          </p>
        )}
        {error && <Notice kind="error">{error}</Notice>}
        <h3 className="consent-scopes-title">Requested permissions</h3>
        <ul className="consent-scopes">
          {requested.map((scope) => (
            <li key={scope}>
              <label className="policy-checkbox">
                <input
                  type="checkbox"
                  checked={accepted.includes(scope)}
                  onChange={(event) =>
                    setAccepted((values) =>
                      event.target.checked
                        ? [...values, scope]
                        : values.filter((value) => value !== scope),
                    )
                  }
                />
                {scopeDescriptions[scope] ?? scope}
              </label>
            </li>
          ))}
        </ul>
        <Notice>
          <ShieldCheck />
          Paid research is disabled until you separately enable this client’s
          per-job cap in connected apps. A paid check also needs an accepted
          quote.
        </Notice>
        <p>
          You can revoke this connection from your account. Web and MCP use the
          same projects, monthly allowances, and purchased credits.
        </p>
        {!requested.length && (
          <Notice kind="error">
            <CircleAlert />
            No scopes were requested. Restart the connection from your client.
          </Notice>
        )}
        <div className="row-actions">
          <Button
            variant="secondary"
            disabled={!!busy || !client}
            busy={busy === "deny"}
            onClick={() => void consent(false)}
          >
            Deny access
          </Button>
          <Button
            disabled={!!busy || !client || !accepted.length}
            busy={busy === "accept"}
            onClick={() => void consent(true)}
          >
            Allow selected access <ArrowRight />
          </Button>
        </div>
      </section>
    </main>
  );
}

export function ContinueAuthorization() {
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let alive = true;
    void authClient.oauth2
      .continue({ selected: true })
      .then((result) => {
        if (!alive) return;
        if (result.error)
          throw new Error(
            result.error.message ?? "Authorization could not continue.",
          );
        const href = authorizationRedirect(result.data?.url);
        if (!href)
          throw new Error(
            "The authorization server did not return a redirect. Restart from your client.",
          );
        location.assign(href);
      })
      .catch((reason) => {
        if (alive) setError(message(reason));
      });
    return () => {
      alive = false;
    };
  }, [revision]);
  return (
    <main className="consent-layout">
      <BrandLogo />
      <div className="consent-panel">
        <h1>Returning to your connection.</h1>
        {error ? (
          <Notice
            kind="error"
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  setError("");
                  setRevision((value) => value + 1);
                }}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Notice>
        ) : (
          <p role="status">Your authorization request is being verified.</p>
        )}
      </div>
    </main>
  );
}
